import React from "react";
import { Box, Typography, Divider, CircularProgress } from "@mui/material";
import { SearchIcon, NoClientUserIcon } from "@/assets/images";

import { t } from "@/utils/i18n";
import HPDropdown from "@/components/common/Dropdown";

import HPInput from "@/components/common/Input";
import { useGetDashboardClientsQuery } from "@/store/api/dashboard-api";
import { User } from "@/hooks/use-share-modal-logic";

interface ShareExistingUsersProps {
  sharedUsers: User[];
  nonSharedUsers: User[];
  allExistingUsers: User[];
  searchTerm: string;
  setSearchTerm: (term: string) => void;
  selectedClientId: string;
  handleClientFilterChange: (clientId: string) => void;
  selectedUsers: string[];
  handleSelectUser: (email: string) => void;
  revocationTarget: string | null;
  confirmRevoke: (email: string) => void;
  cancelRevoke: () => void;
  handleRevokeClick: (email: string) => void;
  renderUserList: (list: User[], isSharedList: boolean) => React.ReactNode;
  dashboardId: string;
  isSearchingUsers: boolean;
  hasInitiallyLoaded: boolean;
}

const ShareExistingUsers: React.FC<ShareExistingUsersProps> = ({
  sharedUsers,
  nonSharedUsers,
  allExistingUsers,
  searchTerm,
  setSearchTerm,
  selectedClientId,
  handleClientFilterChange,
  renderUserList,
  dashboardId,
  isSearchingUsers,
  hasInitiallyLoaded,
}) => {
  const { data: clientsData, isLoading: areClientsLoading } =
    useGetDashboardClientsQuery(dashboardId, {
      skip: !dashboardId,
    });

  const clientOptions = React.useMemo(() => {
    const options = [{ label: "All clients", value: "all" }];

    if (areClientsLoading || !clientsData) {
      return options;
    }

    const allClients = [];
    if (clientsData.primaryClient) {
      allClients.push(clientsData.primaryClient);
    }
    if (clientsData.additionalClients) {
      allClients.push(...clientsData.additionalClients);
    }

    allClients.forEach((client) => {
      options.push({
        label: client.name,
        value: client.id,
      });
    });

    return options;
  }, [clientsData, areClientsLoading]);

  // Show initial loading when we haven't loaded data yet
  const showInitialLoading =
    !hasInitiallyLoaded && (isSearchingUsers || areClientsLoading);

  // Show search loading when we have initially loaded and are actively searching
  const showSearchLoading = hasInitiallyLoaded && isSearchingUsers;

  // Check if there are any users at all (no filters applied)
  const noUsersAtAll =
    hasInitiallyLoaded && !isSearchingUsers && allExistingUsers.length === 0;

  // Check if current filters/search yield no results
  const noSearchResults =
    hasInitiallyLoaded &&
    !isSearchingUsers &&
    !noUsersAtAll &&
    (searchTerm.length > 0 ||
      (selectedClientId !== "all" && selectedClientId !== "")) &&
    sharedUsers.length === 0 &&
    nonSharedUsers.length === 0;

  // Show data when we have loaded and have users to display
  const hasData =
    hasInitiallyLoaded &&
    !noUsersAtAll &&
    (sharedUsers.length > 0 || nonSharedUsers.length > 0 || !noSearchResults);

  const getContentHeight = () => {
    if (showInitialLoading || noUsersAtAll) return "400px";
    return "auto";
  };

  const renderEmptyState = (
    icon: React.ReactNode,
    message: string,
    subMessage?: string,
  ) => (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      sx={{ minHeight: "300px" }}
    >
      {icon}
      <Typography mt={2} className="text-sm font-medium">
        {message}
      </Typography>
      {subMessage && (
        <Typography className="text-sm text-gray-500">{subMessage}</Typography>
      )}
    </Box>
  );

  return (
    <Box
      mt={2}
      sx={{
        minHeight: getContentHeight(),
        transition: "min-height 0.2s ease-in-out",
      }}
    >
      {showInitialLoading ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "400px",
          }}
        >
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Box display="flex" gap={2} my={2} alignItems="end">
            <HPInput
              className="w-full"
              label={t("common.search")}
              placeholder="Search by name, email, company name..."
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              startIcon={<SearchIcon />}
              showClearButton={true}
              disabled={showSearchLoading}
            />
            <HPDropdown
              label="Client company"
              value={selectedClientId}
              options={clientOptions}
              onChange={handleClientFilterChange}
              disabled={areClientsLoading || showSearchLoading}
              placeholder="Select client company"
              multiSelect={false}
              className="min-w-[200px]"
            />
          </Box>

          <Box sx={{ minHeight: "300px", position: "relative" }}>
            {showSearchLoading && (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  minHeight: "300px",
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  backgroundColor: "rgba(255, 255, 255, 0.8)",
                  zIndex: 1,
                }}
              >
                <CircularProgress size={24} />
              </Box>
            )}

            {noUsersAtAll ? (
              renderEmptyState(<NoClientUserIcon />, "No client users yet.")
            ) : noSearchResults ? (
              renderEmptyState(
                <NoClientUserIcon />,
                "No results found.",
                "Try searching with different wording.",
              )
            ) : hasData ? (
              <Box
                sx={{
                  opacity: showSearchLoading ? 0.5 : 1,
                  transition: "opacity 0.2s ease-in-out",
                }}
              >
                <Box
                  display="flex"
                  py={1}
                  px={2}
                  alignItems="center"
                  sx={{ borderBottom: "1px solid #D9D9D9" }}
                >
                  <Box
                    sx={{
                      width: "42px",
                      flexShrink: 0,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  />
                  <Typography variant="subtitle2" sx={{ flex: 1.2, pr: 2 }}>
                    Name
                  </Typography>
                  <Typography variant="subtitle2" sx={{ flex: 2.5, pr: 2 }}>
                    Email
                  </Typography>
                  <Typography variant="subtitle2" sx={{ flex: 1, pr: 2 }}>
                    Account Status
                  </Typography>
                  <Typography variant="subtitle2" sx={{ flex: 1.1, pr: 2 }}>
                    Viewer role state
                  </Typography>
                  <Typography variant="subtitle2" sx={{ flex: 1, pr: 2 }}>
                    Client company
                  </Typography>
                  <Typography variant="subtitle2" sx={{ flex: 1, pr: 2 }}>
                    Last accessed
                  </Typography>
                  <Box sx={{ width: 36, flexShrink: 0, ml: 1 }} />
                </Box>

                {sharedUsers.length > 0 && (
                  <Box
                    mt={2}
                    sx={{
                      paddingBottom: "16px",
                    }}
                  >
                    {renderUserList(sharedUsers, true)}
                  </Box>
                )}

                {nonSharedUsers.length > 0 && (
                  <>
                    {sharedUsers.length > 0 && <Divider />}
                    <Box mt={2}>{renderUserList(nonSharedUsers, false)}</Box>
                  </>
                )}
              </Box>
            ) : null}
          </Box>
        </>
      )}
    </Box>
  );
};

export default ShareExistingUsers;
